/**
 * Where to go after signing in: the page that sent the person to sign in (an emailed link
 * into the app, a printed certificate verification link). Session storage, so it lasts
 * only for the sign-in round trip.
 *
 * Only a same-origin relative path is ever followed, so this cannot be turned into an open
 * redirect: it must start with one "/" (not "//" or "/\"), carry no control characters,
 * resolve to this origin, and not be a sign-in page itself (that would loop).
 */
const KEY = 'kudos.returnTo';
const NOT_A_DESTINATION = ['/login', '/signup', '/auth/callback', '/onboarding'];

export function rememberReturnTo(path: string) {
  try {
    if (isSafeReturnPath(path)) sessionStorage.setItem(KEY, path);
  } catch {
    // Storage blocked: the person lands on the home page instead.
  }
}

/** The remembered path, once; '/' when there is none. */
export function takeReturnTo(): string {
  try {
    const path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return path && isSafeReturnPath(path) ? path : '/';
  } catch {
    return '/';
  }
}

export function isSafeReturnPath(path: string): boolean {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return false;
  let url: URL;
  try {
    url = new URL(path, window.location.origin);
  } catch {
    return false;
  }
  if (url.origin !== window.location.origin) return false;
  return !NOT_A_DESTINATION.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`));
}
